import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import ItemCard from "../components/ItemCard";
import CuratorBadge from "../components/CuratorBadge";
import { Search } from "lucide-react";

const CATEGORIES = ["all", "tops", "bottoms", "outerwear", "dresses", "shoes", "accessories"];
const SIZES = ["all", "XS", "S", "M", "L", "XL", "XXL", "OS"];
const CURATORS = [
  { key: "all", label: "All" },
  { key: "boggy", label: "Boggy's" },
  { key: "fox", label: "Fox's" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [size, setSize] = useState("all");
  const [curator, setCurator] = useState(params.get("curator") || "all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (curator === "all") params.delete("curator"); else params.set("curator", curator);
    setParams(params, { replace: true });
    // eslint-disable-next-line
  }, [curator]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined, category: cat, size };
      if (curator !== "all") params.curator = curator;
      const { data } = await api.get("/items", { params });
      setItems(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, size, curator]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="shop-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <span className="tag-sticker bg-yellow text-xl rotate-tilt-l inline-block mb-2">browse</span>
          <h1 className="font-display text-5xl md:text-6xl uppercase">The rack</h1>
        </div>
        <form onSubmit={onSearch} className="flex items-center gap-2">
          <div className="brut-input flex items-center gap-2 py-2">
            <Search size={18} strokeWidth={3} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="search jeans, tee, levi’s…"
              className="bg-transparent outline-none w-56 md:w-72"
            />
          </div>
          <button type="submit" className="brut-btn bg-orange text-white px-5 py-3" data-testid="search-btn">Find</button>
        </form>
      </div>

      {/* Curator toggle */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="curator-toggle">
        {CURATORS.map((c) => (
          <button
            key={c.key}
            onClick={() => setCurator(c.key)}
            className={`brut-btn px-4 py-2 text-sm flex items-center gap-2 ${curator === c.key ? (c.key === "boggy" ? "bg-green" : c.key === "fox" ? "bg-orange text-white" : "bg-cyan") : "bg-white"}`}
            data-testid={`curator-${c.key}`}
          >
            {c.key !== "all" && <CuratorBadge curator={c.key} />}
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`brut-btn px-4 py-2 text-sm ${cat === c ? "bg-pink text-white" : "bg-white"}`}
            data-testid={`cat-${c}`}
          >
            {c}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-8">
        {SIZES.map((s) => (
          <button
            key={s}
            onClick={() => setSize(s)}
            className={`brut-btn px-3 py-1 text-xs ${size === s ? "bg-cyan" : "bg-white"}`}
            data-testid={`size-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="brut p-10 text-center font-display text-2xl uppercase">Loading the racks…</div>
      ) : items.length === 0 ? (
        <div className="brut p-10 text-center" data-testid="empty-state">
          <p className="font-display text-3xl uppercase">Nothing matches.</p>
          <p className="mt-2">Try clearing filters or different keywords.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="items-grid">
          {items.map((it, i) => <ItemCard key={it.id} item={it} idx={i} />)}
        </div>
      )}
    </div>
  );
}
